import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeftRight, ScanLine, Search, SlidersHorizontal } from 'lucide-react'
import { getInventoryItems, resolveProductByCode } from '../../api/inventory'
import BarcodeScanner from '../../components/BarcodeScanner'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { useSession } from '../../session'
import { money } from '../sales/salesShared'
import InventoryTabs from './InventoryTabs'
import { qty, stockItems, stockState, stockValue, unitCost, useLocations } from './inventoryShared'

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'ok', label: 'In stock' },
  { id: 'low', label: 'Running low' },
  { id: 'out', label: 'Out of stock' },
]

// Every stock item with how many are on hand and what they're worth.
// Type or scan a barcode to jump straight to an item. With Production, raw
// materials have their own tab (materials): Stock is then what you sell,
// bought to resell or made here.
export default function StockList({ materials = false }) {
  const nav = useNavigate()
  const { has } = useSession()
  const split = has('production')
  const locations = useLocations()
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState('')
  const filter = FILTERS.some((f) => f.id === params.get('show')) ? params.get('show') : 'all'
  const setFilter = (id) => setParams(id === 'all' ? {} : { show: id }, { replace: true })

  useEffect(() => {
    getInventoryItems()
      .then((data) => setRows(stockItems(data).filter((r) => (materials ? r.stock_kind === 'material' : !(split && r.stock_kind === 'material')))))
      .catch((e) => setError(e.message || 'Could not load stock.'))
      .finally(() => setLoading(false))
  }, [materials, split])

  const counts = useMemo(() => Object.fromEntries(
    FILTERS.map((f) => [f.id, rows.filter((r) => f.id === 'all' || stockState(r).id === f.id).length])
  ), [rows])

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return rows
      .filter((r) => (filter === 'all' || stockState(r).id === filter)
        && (!term || [r.name, r.sku, r.barcode].join(' ').toLowerCase().includes(term)))
      .sort((a, b) => String(a.name || a.sku).localeCompare(String(b.name || b.sku)))
  }, [rows, filter, search])

  // An exact SKU or barcode opens that item.
  const openCode = async (code) => {
    const c = String(code || '').trim()
    if (!c) return
    setError('')
    const local = rows.find((r) => [r.sku, r.barcode].some((v) => String(v || '').toLowerCase() === c.toLowerCase()))
    if (local) { nav(`/inventory/stock/${local.product_id}`); return }
    try {
      const p = await resolveProductByCode(c)
      if (p?.product_id) { nav(`/inventory/stock/${p.product_id}`); return }
    } catch { /* fall through */ }
    setSearch(c)
    setError(`No product has the code ${c}.`)
  }

  return (
    <div className="ci-page">
      <InventoryTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>{materials ? 'Raw materials' : 'Stock'}</h1>
            <div className="ci-title-meta">{materials
              ? 'The materials you keep to make your products, how much you have and what it’s worth.'
              : split ? 'What you sell, bought to resell or made here: how many you have and what they cost you.' : 'How many of each product you have and what they cost you.'}</div>
          </div>
        </div>
        <div className="ci-header-actions">
          {materials && <Link className="ci-btn ci-btn-ghost" to="/purchase/po-form/new?for=materials">Order materials</Link>}
          {locations?.length > 1 && <Link className="ci-btn ci-btn-ghost" to="/inventory/move"><ArrowLeftRight size={15} />Move stock</Link>}
          <Link className="ci-btn ci-btn-primary" to="/inventory/adjust"><SlidersHorizontal size={15} />Adjust stock</Link>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="ci-toolbar">
        <form className="ci-search" aria-label="Search stock" onSubmit={(e) => { e.preventDefault(); if (shown.length === 1) nav(`/inventory/stock/${shown[0].product_id}`); else openCode(search) }}>
          <Search size={14} />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setError('') }} placeholder="Name, SKU or barcode…" />
        </form>
        <button type="button" className="ci-btn ci-btn-ghost" onClick={() => setScanning(true)}><ScanLine size={15} />Scan</button>
      </div>

      <FilterChips options={FILTERS.map((f) => ({ ...f, count: counts[f.id] }))} value={filter} onChange={setFilter} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>Product</th>
                <th className="num">On hand</th>
                <th className="num hide-sm">Cost each</th>
                <th className="num">Value</th>
                <th className="num hide-sm">Price</th>
                <th className="center">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6"><Spinner label="Loading stock…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">{rows.length ? (materials ? 'No materials match.' : 'No products match.') : materials ? 'No materials yet. Mark a product as a material under Production, What you make, or add one when you order it.' : 'No products yet. Add them under Master Files, Products & services.'}</div></td></tr>
              ) : shown.map((r) => {
                const st = stockState(r)
                return (
                  <tr key={r.product_id} className="row-link" onClick={() => nav(`/inventory/stock/${r.product_id}`)}>
                    <td>
                      <Link className="ci-je-link" to={`/inventory/stock/${r.product_id}`} onClick={(e) => e.stopPropagation()}>{r.name || r.sku}</Link>
                      <span className="cell-sub">{r.sku}{split && !materials && r.stock_kind === 'made' ? ' · Made here' : ''}</span>
                    </td>
                    <td className="num ci-strong">{qty(r.qty_on_hand)}</td>
                    <td className="num hide-sm">{money(unitCost(r))}</td>
                    <td className="num ci-total">{money(stockValue(r))}</td>
                    <td className="num ci-muted-cell hide-sm">{Number(r.unit_price) > 0 ? money(r.unit_price) : '–'}</td>
                    <td className="center"><StatusBadge label={st.label} tone={st.tone} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} {materials ? (shown.length === 1 ? 'material' : 'materials') : shown.length === 1 ? 'product' : 'products'} · worth {money(shown.reduce((s, r) => s + stockValue(r), 0))}</span>
          <Link className="ci-page-info" to="/inventory/cost-layers">Cost batches report</Link>
        </div>
      </div>

      <BarcodeScanner open={scanning} onClose={() => setScanning(false)} onScan={(code) => { setScanning(false); openCode(code) }} />
    </div>
  )
}
